import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { Creature } from '../../types';
import { formatSoldierType } from '../../utils/formatting';
import { useIceSurface } from '../../hooks/useIceSurface';
import { StatColumn } from '../shared/StatColumn';
import { createSubtleIceSurface, hashStringToSeed } from '../../theme/surfaces';
import { ItemDetailsPopover } from '../shared/ItemDetailsPopover';
import { PopoverState } from '../../hooks/useItemPopover';

interface CreateSoldierModalProps {
  createAsTemporary?: boolean;
}

type SoldierTabType = 'StandardSoldier' | 'SpecialistSoldier' | 'Creature';

const tabTypes: Array<{ label: string; type: SoldierTabType }> = [
  { label: formatSoldierType('StandardSoldier', true), type: 'StandardSoldier' },
  { label: formatSoldierType('SpecialistSoldier', true), type: 'SpecialistSoldier' },
  { label: formatSoldierType('Creature', true), type: 'Creature' },
];

export const CreateSoldierModal: React.FC<CreateSoldierModalProps> = ({ createAsTemporary = false }) => {
  const {
    ui,
    selectedWarbandId,
    warbands,
    closeCreateSoldierModal,
    baseResources,
  } = useWarbandStore();

  const gold = warbands.find((w) => w.id === selectedWarbandId)?.gold ?? 0;
  const soldierDiscount = baseResources.reduce((total, r) => total + (r.soldierHireDiscount ?? 0), 0);

  const [activeTab, setActiveTab] = React.useState(0);
  const tabsSurface = useIceSurface({ icicles: true, card: true });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isFree, setIsFree] = React.useState(false);
  const [traitPopover, setTraitPopover] = React.useState<PopoverState | null>(null);

  const creatures = useReferenceDataStore((s) => s.creatures);
  const creaturesByType = React.useMemo<Record<SoldierTabType, Creature[]>>(() => ({
    StandardSoldier: creatures.filter((c) => c.type === 'StandardSoldier'),
    SpecialistSoldier: creatures.filter((c) => c.type === 'SpecialistSoldier'),
    Creature: creatures.filter((c) => c.type === 'Creature'),
  }), [creatures]);

  const [selectedCreatureIds, setSelectedCreatureIds] = React.useState<Partial<Record<SoldierTabType, string>>>({});

  // Initialise default selections when creatures become available
  React.useEffect(() => {
    setSelectedCreatureIds({
      StandardSoldier: creaturesByType.StandardSoldier[0]?.id,
      SpecialistSoldier: creaturesByType.SpecialistSoldier[0]?.id,
      Creature: creaturesByType.Creature[0]?.id,
    });
  }, [creaturesByType]);

  const handleClose = () => {
    setError(null);
    setSubmitting(false);
    setIsFree(false);
    closeCreateSoldierModal();
  };

  const currentTabType = tabTypes[activeTab].type;
  const currentCreatures = creaturesByType[currentTabType] || [];
  const selectedCreatureId = selectedCreatureIds[currentTabType];
  const selectedCreature = currentCreatures.find((creature) => creature.id === selectedCreatureId);

  const canAfford = isFree || !selectedCreature || selectedCreature.cost === 0 || gold >= Math.max(0, selectedCreature.cost - soldierDiscount);

  const handleCreate = async () => {
    if (!selectedWarbandId) {
      setError('No party selected');
      return;
    }

    if (!selectedCreature) {
      setError('Please select a soldier');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      useDirtyTrackingStore.getState().queueSoldierCreate(selectedCreature, createAsTemporary, isFree);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add soldier');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={ui.createSoldierModalOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}

        <Tabs
          value={activeTab}
          onChange={(_, value: number) => setActiveTab(value)}
          aria-label="soldier type tabs"
          sx={{ ...tabsSurface }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile={true}
        >
          {tabTypes.map((tab) => (
            <Tab key={tab.type} label={tab.label} />
          ))}
        </Tabs>

        <Stack spacing={2}>
            {currentCreatures.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[...currentCreatures].sort((a, b) => a.name.localeCompare(b.name)).map((creature) => (
                  <Chip
                    key={creature.id}
                    label={creature.name}
                    color={selectedCreatureId === creature.id ? 'primary' : 'default'}
                    onClick={() =>
                      setSelectedCreatureIds((previous) => ({
                        ...previous,
                        [currentTabType]: creature.id,
                      }))
                    }
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No soldiers found for this type.
              </Typography>
            )}

            {selectedCreature && (
              <Card variant="outlined"
                sx={{  ...createSubtleIceSurface(hashStringToSeed(selectedCreature.id), { }) }}
                >
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {selectedCreature.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Type: {formatSoldierType(selectedCreature.type, true)}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(6, 1fr)' },
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <StatColumn label="Move" value={selectedCreature.move} adjustedValue={selectedCreature.move}/>
                      <StatColumn label="Fight" value={selectedCreature.fight} adjustedValue={selectedCreature.fight}/>
                      <StatColumn label="Shoot" value={selectedCreature.shoot} adjustedValue={selectedCreature.shoot}/>
                      <StatColumn label="Armour" value={selectedCreature.armour} adjustedValue={selectedCreature.armour}/>
                      <StatColumn label="Will" value={selectedCreature.will} adjustedValue={selectedCreature.will}/>
                      <StatColumn label="Health" value={selectedCreature.health} adjustedValue={selectedCreature.health}/>
                    </Box>
                    <Typography variant="body2" color={canAfford ? 'text.secondary' : 'error'}>
                      Cost: {Math.max(0, selectedCreature.cost - soldierDiscount)}gc
                      {soldierDiscount > 0 && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          (-{soldierDiscount}gc Carrier Pigeons)
                        </Typography>
                      )}
                      {!canAfford && ` (have ${gold}gc)`}
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                        gap: 2,
                        alignItems: 'start',
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Default Weapons
                          </Typography>
                          {selectedCreature.weapons.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selectedCreature.weapons.map((weapon) => (
                                <Chip key={weapon.id} size="small" label={weapon.name} />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">None</Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Default Armour
                          </Typography>
                          {selectedCreature.armourItems.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selectedCreature.armourItems.map((armour) => (
                                <Chip key={armour.id} size="small" label={armour.name} />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">None</Typography>
                          )}
                        </Box>

                        {selectedCreature.treasureItems.length > 0 && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Default Items
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selectedCreature.treasureItems.map((item) => (
                                <Chip key={item.id} size="small" label={item.name} />
                              ))}
                            </Box>
                          </Box>
                        )}

                        {selectedCreature.traits.length > 0 && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Traits
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selectedCreature.traits.map((trait) => (
                                <Chip
                                  key={trait.id}
                                  size="small"
                                  label={trait.name}
                                  onClick={(e) => setTraitPopover({
                                    anchor: e.currentTarget,
                                    name: trait.name,
                                    description: trait.description ?? null,
                                    tags: [],
                                  })}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Stack>

                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Notes
                        </Typography>
                        <Typography variant="body2">
                          {selectedCreature.notes || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        <ItemDetailsPopover popover={traitPopover} onClose={() => setTraitPopover(null)} />
      </DialogContent>
      <DialogActions>
        {selectedCreature && selectedCreature.cost > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isFree}
                onChange={(e) => setIsFree(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Add for free</Typography>}
            sx={{ mr: 'auto' }}
          />
        )}
        <Button onClick={handleClose} disabled={submitting}>Close</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={submitting || !selectedCreature || !canAfford}
        >
          {submitting ? <CircularProgress size={18} /> : createAsTemporary ? 'Add Temporary Soldier' : 'Add Soldier'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
